import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import DateRangePicker from '../components/analytics/DateRangePicker';
import { useDeliveriesReport } from '../hooks/useDeliveriesReport';
import {
  DIMENSION_COLUMN_HEADERS,
  DIMENSION_SECONDARY_COLUMN_HEADERS,
  DIMENSION_LABELS,
  ReportDimension,
  getComboProductQuantity,
} from '../utils/deliveriesReportUtils';
import {
  companiesService,
  publishersService,
  productsService,
  combosService,
  type Company,
  type Publisher,
  type Product,
  type Combo,
} from '../services/firebaseFunctions';
import { formatCnpj } from '../utils/formatCnpj';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toDateString(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

const DeliveriesReportPage: React.FC = () => {
  const hook = useDeliveriesReport();

  // Estado local do DateRangePicker (trabalha com Date objects)
  const [pickerStart, setPickerStart] = useState<Date | null>(null);
  const [pickerEnd, setPickerEnd] = useState<Date | null>(null);

  // Snackbar de feedback de erro
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error' as 'error' | 'success',
  });

  // Listas para os filtros em typeahead (Autocomplete) — mesmo padrão de DeliveryQueuePage.tsx
  const [companies, setCompanies] = useState<Company[]>([]);
  const [publishers, setPublishers] = useState<Publisher[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [combos, setCombos] = useState<Combo[]>([]);

  useEffect(() => {
    companiesService.getAll().then(setCompanies).catch(() => setCompanies([]));
    publishersService.getAll().then(setPublishers).catch(() => setPublishers([]));
    productsService.getAll().then(setProducts).catch(() => setProducts([]));
    combosService.getAll().then(setCombos).catch(() => setCombos([]));
  }, []);

  // Abrir Snackbar quando um novo erro aparece no hook
  const prevErrorRef = useRef<string | null>(null);
  useEffect(() => {
    if (hook.error && hook.error !== prevErrorRef.current) {
      prevErrorRef.current = hook.error;
      setSnackbar({ open: true, message: hook.error, severity: 'error' });
    }
    if (!hook.error) {
      prevErrorRef.current = null;
    }
  }, [hook.error]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleDateChange = (start: Date, end: Date) => {
    setPickerStart(start);
    setPickerEnd(end);
    hook.setDateStart(toDateString(start));
    hook.setDateEnd(toDateString(end));
  };

  const handleDimensionChange = (
    _: React.MouseEvent<HTMLElement>,
    value: ReportDimension | null
  ) => {
    if (value) {
      hook.setDimension(value);
    }
  };

  const canGenerate = !hook.loading && !!hook.dateStart && !!hook.dateEnd;
  const isComboDimension = hook.dimension === 'combo';
  const isCompanyDimension = hook.dimension === 'company';
  const companiesById = useMemo(() => new Map(companies.map((c) => [c.id, c])), [companies]);

  const getCompanyCnpjDisplay = (companyId: string): string => {
    const cnpj = companiesById.get(companyId)?.cnpj;
    return cnpj ? formatCnpj(cnpj) : '-';
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Box p={3}>
      {/* Cabeçalho */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Typography variant="h4" sx={{ fontWeight: 600, flex: 1 }}>
          Relatórios de Entregas
        </Typography>
      </Box>

      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
        Selecione a dimensão de agrupamento, o período e os filtros opcionais para gerar o
        relatório de entregas.
      </Typography>

      {/* Painel de configuração */}
      <Paper sx={{ p: 3, mb: 3 }}>
        {/* Seletor de dimensão */}
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
          Dimensão
        </Typography>
        <ToggleButtonGroup
          value={hook.dimension}
          exclusive
          onChange={handleDimensionChange}
          size="small"
          sx={{ mb: 3 }}
          aria-label="dimensão do relatório"
        >
          {(Object.entries(DIMENSION_LABELS) as [ReportDimension, string][]).map(
            ([key, label]) => (
              <ToggleButton key={key} value={key} aria-label={label}>
                {label}
              </ToggleButton>
            )
          )}
        </ToggleButtonGroup>

        {/* Período */}
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
          Período
        </Typography>
        <Box sx={{ mb: 3 }}>
          <DateRangePicker
            startDate={pickerStart}
            endDate={pickerEnd}
            onDateChange={handleDateChange}
            loading={hook.loading}
            maxIntervalDays={365}
          />
        </Box>

        {/* Filtros opcionais */}
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
          Filtros opcionais
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end', mb: 3 }}>
          <Autocomplete<Product, false, false, false>
            size="small"
            sx={{ minWidth: 220 }}
            options={products}
            value={products.find((p) => p.id === hook.filters.productId) ?? null}
            onChange={(_, newValue) => hook.setFilter('productId', newValue?.id ?? '')}
            getOptionLabel={(o) => o.titulo}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            noOptionsText="Nenhum ativo encontrado"
            renderInput={(params) => (
              <TextField {...params} label="Tipo de Ativo" placeholder="Todos" />
            )}
          />
          <Autocomplete<Company, false, false, false>
            size="small"
            sx={{ minWidth: 220 }}
            options={companies}
            value={companies.find((c) => c.id === hook.filters.companyId) ?? null}
            onChange={(_, newValue) => hook.setFilter('companyId', newValue?.id ?? '')}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            noOptionsText="Nenhuma empresa encontrada"
            renderInput={(params) => (
              <TextField {...params} label="Empresa" placeholder="Todas" />
            )}
          />
          <Autocomplete<Publisher, false, false, false>
            size="small"
            sx={{ minWidth: 220 }}
            options={publishers}
            value={publishers.find((p) => p.id === hook.filters.publisherId) ?? null}
            onChange={(_, newValue) => hook.setFilter('publisherId', newValue?.id ?? '')}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            noOptionsText="Nenhuma editora encontrada"
            renderInput={(params) => (
              <TextField {...params} label="Editora" placeholder="Todas" />
            )}
          />
          <Autocomplete<Combo, false, false, false>
            size="small"
            sx={{ minWidth: 220 }}
            options={combos}
            value={combos.find((c) => c.id === hook.filters.comboId) ?? null}
            onChange={(_, newValue) => hook.setFilter('comboId', newValue?.id ?? '')}
            getOptionLabel={(o) => `${o.nome} (${o.codigo})`}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            noOptionsText="Nenhum combo encontrado"
            renderInput={(params) => (
              <TextField {...params} label="Combo" placeholder="Todos" />
            )}
          />
          <Button variant="text" size="small" onClick={hook.clearFilters}>
            Limpar filtros
          </Button>
        </Box>

        {/* Botões de ação */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button
            variant="contained"
            onClick={hook.generateReport}
            disabled={!canGenerate}
            startIcon={
              hook.loading && !hook.exporting ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            {hook.loading && !hook.exporting ? 'Gerando...' : 'Gerar Relatório'}
          </Button>

          <Button
            variant="outlined"
            onClick={hook.exportCsv}
            disabled={!hook.hasGenerated || hook.exporting}
            startIcon={
              hook.exporting ? <CircularProgress size={16} color="inherit" /> : undefined
            }
          >
            {hook.exporting ? 'Gerando CSV...' : 'Exportar CSV'}
          </Button>
        </Box>
      </Paper>

      {/* Seção de resultados — aparece a partir do primeiro fetch (loading ou dados) */}
      {(hook.loading || hook.hasGenerated) && (
        <>
          {/* Resumo global */}
          {hook.summary !== null && !hook.loading && (
            <Typography variant="body1" sx={{ mb: 1 }}>
              Total de entregas:{' '}
              <strong>{hook.summary.totalDeliveries.toLocaleString('pt-BR')}</strong>
            </Typography>
          )}

          <Paper>
            {/* Loading durante geração / troca de página */}
            {hook.loading ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: 200,
                }}
              >
                <CircularProgress />
              </Box>
            ) : hook.rows.length === 0 ? (
              /* Estado vazio */
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  Nenhuma entrega encontrada para os filtros e período selecionados.
                </Typography>
              </Box>
            ) : (
              /* Tabela com dados */
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {isCompanyDimension && (
                        <TableCell sx={{ fontWeight: 600 }}>CNPJ</TableCell>
                      )}
                      <TableCell sx={{ fontWeight: 600 }}>
                        {DIMENSION_COLUMN_HEADERS[hook.dimension]}
                      </TableCell>
                      {isComboDimension ? (
                        hook.productColumns.map((col) => (
                          <TableCell key={col.productId} sx={{ fontWeight: 600 }} align="right">
                            {col.productName}
                          </TableCell>
                        ))
                      ) : (
                        DIMENSION_SECONDARY_COLUMN_HEADERS[hook.dimension] !== null && (
                          <TableCell sx={{ fontWeight: 600 }}>
                            {DIMENSION_SECONDARY_COLUMN_HEADERS[hook.dimension]}
                          </TableCell>
                        )
                      )}
                      <TableCell sx={{ fontWeight: 600 }} align="right">
                        {isComboDimension ? 'Total' : 'Quantidade'}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {hook.rows.map((row, index) => (
                      <TableRow
                        key={`${row.dimensionId}-${row.secondaryId ?? ''}-${index}`}
                        hover
                      >
                        {isCompanyDimension && (
                          <TableCell>{getCompanyCnpjDisplay(row.dimensionId)}</TableCell>
                        )}
                        <TableCell>{row.dimensionName}</TableCell>
                        {isComboDimension ? (
                          hook.productColumns.map((col) => (
                            <TableCell key={col.productId} align="right">
                              {getComboProductQuantity(row, col.productId).toLocaleString('pt-BR')}
                            </TableCell>
                          ))
                        ) : (
                          DIMENSION_SECONDARY_COLUMN_HEADERS[hook.dimension] !== null && (
                            <TableCell>{row.secondaryName}</TableCell>
                          )
                        )}
                        <TableCell align="right">
                          {row.quantity.toLocaleString('pt-BR')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Paginação server-side — visível após ao menos uma geração bem-sucedida */}
            {hook.hasGenerated && !hook.loading && (
              <TablePagination
                component="div"
                count={hook.pagination?.totalRows ?? 0}
                page={hook.page - 1} // MUI é 0-based; estado interno é 1-based
                rowsPerPage={hook.pageSize}
                rowsPerPageOptions={[10, 25, 50, 100, 200]}
                onPageChange={(_, newPage) => hook.setPage(newPage + 1)}
                onRowsPerPageChange={(e) =>
                  hook.setPageSize(parseInt(e.target.value, 10))
                }
                labelRowsPerPage="Linhas por página:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}–${to} de ${count !== -1 ? count.toLocaleString('pt-BR') : `mais de ${to}`}`
                }
              />
            )}
          </Paper>
        </>
      )}

      {/* Snackbar de feedback (erros de geração / exportação) */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DeliveriesReportPage;
